import axiosClient from '../api/axiosClient';
import inventoryApi from './inventoryApi';

const formatMoney = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  return `$${(n / 100).toFixed(2)}`;
};

const getBookingClientLabel = (booking) => {
  const name =
    booking?.client_name ||
    booking?.client?.name ||
    booking?.Client?.name ||
    booking?.customer_name ||
    booking?.customer?.name;
  if (name && String(name).trim()) return String(name).trim();

  // If the booking is missing a client, show a professional fallback (never "Unknown Client")
  if (booking?.id != null) return `Booking #${booking.id}`;
  return 'Booking';
};

class SearchService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Clear expired cache entries
  clearExpiredCache() {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > this.cacheTimeout) {
        this.cache.delete(key);
      }
    }
  }

  // Get cached data or fetch new data
  async getCachedData(key, fetchFunction) {
    this.clearExpiredCache();
    
    if (this.cache.has(key)) {
      return this.cache.get(key).data;
    }

    try {
      const data = await fetchFunction();
      this.cache.set(key, {
        data,
        timestamp: Date.now()
      });
      return data;
    } catch (error) {
      console.error(`Error fetching data for key ${key}:`, error);
      return [];
    }
  }

  // Search bookings
  async searchBookings(query) {
    if (!query || query.length < 2) return [];
    
    return this.getCachedData(`bookings_${query}`, async () => {
      try {
        const params = new URLSearchParams({
          page: '1',
          pageSize: '10',
          q: query
        });
        const response = await axiosClient.get(`/admin/bookings?${params.toString()}`);

        const items = response.data?.items || response.data?.data || response.data || [];

        return (Array.isArray(items) ? items : []).map((booking) => {
          const clientLabel = getBookingClientLabel(booking);
          const serviceLabel =
            booking?.service_name ||
            booking?.service?.name ||
            booking?.Service?.name ||
            'Service';
          const barberLabel =
            booking?.barber_name ||
            booking?.barber?.name ||
            booking?.Barber?.name ||
            'Barber';

          return {
            id: booking.id,
            type: 'booking',
            title: `${clientLabel} • ${serviceLabel}`,
            subtitle: `${barberLabel} • ${booking?.start_local ? new Date(booking.start_local).toLocaleString() : 'No date/time'}`,
            status: booking.status || 'active',
            url: `/admin/bookings?q=${encodeURIComponent(query)}&focus_booking_id=${booking.id}`,
            icon: 'calendar'
          };
        });
      } catch (error) {
        console.error('Error searching bookings:', error);
        return [];
      }
    });
  }

  // Search clients
  async searchClients(query) {
    if (!query || query.length < 2) return [];
    
    return this.getCachedData(`clients_${query}`, async () => {
      try {
        const params = new URLSearchParams({
          page: '1',
          pageSize: '10',
          q: query,
          status: 'active'
        });
        const response = await axiosClient.get(`/admin/clients?${params.toString()}`);
        return (response.data.data || []).map(client => ({
          id: client.id,
          type: 'client',
          title: client.name || 'Unknown Client',
          subtitle: `${client.email || 'No email'} • ${client.phone || 'No phone'}`,
          status: client.status,
          url: `/admin/clients?q=${encodeURIComponent(query)}&focus_client_id=${client.id}`,
          icon: 'users'
        }));
      } catch (error) {
        console.error('Error searching clients:', error);
        return [];
      }
    });
  }

  // Search barbers
  async searchBarbers(query) {
    if (!query || query.length < 2) return [];
    
    return this.getCachedData(`barbers_${query}`, async () => {
      try {
        const params = new URLSearchParams({
          page: '1',
          pageSize: '10',
          q: query,
          status: 'active'
        });
        const response = await axiosClient.get(`/admin/barbers?${params.toString()}`);
        return (response.data.data || []).map(barber => ({
          id: barber.id,
          type: 'barber',
          title: barber.name || 'Unknown Barber',
          subtitle: `${barber.email || 'No email'} • ${barber.specialties || 'No specialties'}`,
          status: barber.status,
          url: `/admin/barbers?q=${encodeURIComponent(query)}&focus_barber_id=${barber.id}`,
          icon: 'scissors'
        }));
      } catch (error) {
        console.error('Error searching barbers:', error);
        return [];
      }
    });
  }

  // Search services
  async searchServices(query) {
    if (!query || query.length < 2) return [];
    
    return this.getCachedData(`services_${query}`, async () => {
      try {
        const params = new URLSearchParams({
          page: '1',
          pageSize: '10',
          q: query,
          status: 'all'
        });
        const response = await axiosClient.get(`/admin/services?${params.toString()}`);
        const services = response.data?.items || response.data?.data || response.data || [];

        return (Array.isArray(services) ? services : []).map(service => ({
          id: service.id,
          type: 'service',
          title: service.name || 'Unknown Service',
          subtitle: `${service.description || 'No description'}${formatMoney(service.price_cents) ? ` • ${formatMoney(service.price_cents)}` : ''}`,
          status: service.is_active === false ? 'disabled' : 'active',
          url: `/admin/services?q=${encodeURIComponent(query)}&focus_service_id=${service.id}`,
          icon: 'wrench'
        }));
      } catch (error) {
        console.error('Error searching services:', error);
        return [];
      }
    });
  }

  // Search inventory items
  async searchInventory(query) {
    if (!query || query.length < 2) return [];
    
    return this.getCachedData(`inventory_${query}`, async () => {
      try {
        const response = await inventoryApi.items.getItems({ query, page: 1, pageSize: 10 });
        const items = response.items || [];
        
        return items.map(item => ({
          id: item.id,
          type: 'inventory',
          title: item.name || 'Unknown Item',
          subtitle: `${item.category || 'Uncategorized'}`,
          status: 'active',
          url: `/admin/inventory/products/${item.id}`,
          icon: 'box'
        }));
      } catch (error) {
        console.error('Error searching inventory:', error);
        return [];
      }
    });
  }

  // Search AI-generated hairstyles and client history
  async searchAITools(query) {
    if (!query || query.length < 2) return [];
    
    return this.getCachedData(`ai_${query}`, async () => {
      try {
        // Search through client hairstyle history
        const response = await axiosClient.get(`/admin/clients?q=${encodeURIComponent(query)}&pageSize=5`);
        const clients = response.data.data || [];
        
        // Return clients who have AI-generated images
        return clients.filter(client => client.has_ai_images).map(client => ({
          id: client.id,
          type: 'ai-tool',
          title: `${client.name}'s AI Hairstyles`,
          subtitle: `View AI-generated hairstyles and analysis`,
          status: 'active',
          url: `/admin/ai-analysis?client=${client.id}`,
          icon: 'sparkles'
        }));
      } catch (error) {
        console.error('Error searching AI tools:', error);
        return [];
      }
    });
  }

  // Perform global search across all entities
  async globalSearch(query, options = {}) {
    if (!query || query.length < 2) return [];

    try {
      const {
        includeBookings = true,
        includeClients = true,
        includeBarbers = true,
        includeServices = true,
        includeInventory = true,
        includeAiTools = true,
      } = options || {};

      // Only call endpoints the current user is allowed to access.
      // This prevents noisy 403s in the console for roles without CRM permissions.
      const [bookings, clients, barbers, services, inventory, aiTools] = await Promise.all([
        includeBookings ? this.searchBookings(query) : Promise.resolve([]),
        includeClients ? this.searchClients(query) : Promise.resolve([]),
        includeBarbers ? this.searchBarbers(query) : Promise.resolve([]),
        includeServices ? this.searchServices(query) : Promise.resolve([]),
        includeInventory ? this.searchInventory(query) : Promise.resolve([]),
        includeAiTools ? this.searchAITools(query) : Promise.resolve([]),
      ]);

      // Combine and prioritize results by relevance
      const allResults = [
        ...(Array.isArray(clients) ? clients.slice(0, 2) : []),      // Clients first (most important)
        ...(Array.isArray(bookings) ? bookings.slice(0, 2) : []),    // Then bookings
        ...(Array.isArray(barbers) ? barbers.slice(0, 2) : []),      // Barbers
        ...(Array.isArray(services) ? services.slice(0, 2) : []),    // Services
        ...(Array.isArray(inventory) ? inventory.slice(0, 1) : []),  // Inventory
        ...(Array.isArray(aiTools) ? aiTools.slice(0, 1) : []),      // AI Tools
      ];

      // Return up to 10 total results with variety
      return allResults.slice(0, 10);
    } catch (error) {
      console.error('Error in global search:', error);
      return [];
    }
  }

  // Clear all cache
  clearCache() {
    this.cache.clear();
  }
}

const searchService = new SearchService();

export default searchService;
